import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { SupplierFormFields, SupplierFormValues } from "./SupplierFormFields";
import { Supplier } from "@/hooks/useSuppliers";

const supplierSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, "Informe o nome")
    .max(120, "Nome muito longo"),
  document: z.string().trim().max(30, "Documento muito longo").optional(),
  phone: z.string().trim().max(30, "Telefone muito longo").optional(),
  email: z
    .string()
    .trim()
    .max(255, "E-mail muito longo")
    .refine(
      (val) => !val || z.string().email().safeParse(val).success,
      "E-mail inválido"
    )
    .optional(),
});

interface SupplierFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: "create" | "edit";
  supplier?: Supplier | null;
  onCreate: (values: { name: string; document?: string; phone?: string; email?: string; chart_account_id?: string }) => Promise<string | null>;
  onUpdate: (id: string, values: { name: string; document?: string; phone?: string; email?: string; chart_account_id?: string }) => Promise<boolean>;
}

export function SupplierFormDialog({
  open,
  onOpenChange,
  mode,
  supplier,
  onCreate,
  onUpdate,
}: SupplierFormDialogProps) {
  const [values, setValues] = useState<SupplierFormValues>({
    name: "",
    document: "",
    phone: "",
    email: "",
    chartAccountId: "__none__",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && supplier) {
      setValues({
        name: supplier.name,
        document: supplier.document || "",
        phone: supplier.phone || "",
        email: supplier.email || "",
        chartAccountId: supplier.chart_account_id || "__none__",
      });
    } else {
      setValues({ name: "", document: "", phone: "", email: "", chartAccountId: "__none__" });
    }
  }, [open, mode, supplier]);

  const payload = useMemo(
    () => ({
      name: values.name.trim(),
      document: values.document.trim() || undefined,
      phone: values.phone.trim() || undefined,
      email: values.email.trim() || undefined,
      chart_account_id: values.chartAccountId === "__none__" ? undefined : values.chartAccountId,
    }),
    [values]
  );

  const canSubmit = values.name.trim().length >= 2;

  const handleSubmit = async () => {
    const parsed = supplierSchema.safeParse(payload);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Dados inválidos");
      return;
    }

    setSubmitting(true);
    try {
      if (mode === "edit" && supplier) {
        const success = await onUpdate(supplier.id, payload);
        if (success) onOpenChange(false);
      } else {
        const id = await onCreate(payload);
        if (id) onOpenChange(false);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === "edit" ? "Editar fornecedor/credor" : "Novo fornecedor/credor"}
          </DialogTitle>
          <DialogDescription>
            {mode === "edit"
              ? "Atualize os dados do fornecedor/credor."
              : "Cadastre um novo fornecedor ou credor para vincular às despesas."}
          </DialogDescription>
        </DialogHeader>

        <SupplierFormFields
          values={values}
          onChange={setValues}
          autoFocusName={mode === "create"}
        />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}>
            {submitting ? "Salvando..." : mode === "edit" ? "Salvar" : "Cadastrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
